import { useState } from "react";
import { Copy, Check, Mail, Server, Shield, Wifi } from "lucide-react";

interface Setting { label: string; value: string }

function CopyButton({ text }: { text: string }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };
    return (
        <button
            type="button"
            onClick={copy}
            className="ml-2 rounded p-0.5 text-muted-foreground hover:text-foreground"
            aria-label={`Copy ${text}`}
        >
            {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
    );
}

function SettingsRow({ label, value }: Setting) {
    return (
        <div className="flex items-center justify-between border-b py-2.5 last:border-0">
            <span className="text-sm text-muted-foreground">{label}</span>
            <span className="flex items-center font-mono text-sm font-medium">
                {value}
                <CopyButton text={value} />
            </span>
        </div>
    );
}

function SettingsCard({
    icon,
    title,
    badge,
    rows
}: {
    icon: React.ReactNode;
    title: string;
    badge: string;
    rows: Setting[];
}) {
    return (
        <div className="rounded-lg border bg-card p-4">
            <div className="mb-3 flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                    {icon}
                </span>
                <div>
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm">{title}</span>
                        <span className="rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                            {badge}
                        </span>
                    </div>
                </div>
            </div>
            <div className="divide-y">
                {rows.map((r) => <SettingsRow key={r.label} {...r} />)}
            </div>
        </div>
    );
}

const HOST = "mx1.athavita.com";

export function MailClientPage() {
    const [tab, setTab] = useState<"thunderbird" | "outlook" | "ios" | "android">("thunderbird");

    const tabs: Array<{ id: typeof tab; label: string }> = [
        { id: "thunderbird", label: "Thunderbird" },
        { id: "outlook", label: "Outlook" },
        { id: "ios", label: "iOS Mail" },
        { id: "android", label: "Gmail / Android" },
    ];

    return (
        <div className="max-w-2xl space-y-6">
            <div>
                <h2 className="text-lg font-semibold">Email client setup</h2>
                <p className="mt-0.5 text-sm text-muted-foreground">
                    Connect Outlook, Thunderbird, or your phone's mail app using these settings.
                    Use your Athavita email address as the username.
                </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
                <SettingsCard
                    icon={<Wifi className="h-4 w-4" />}
                    title="Incoming mail (IMAP)"
                    badge="Recommended"
                    rows={[
                        { label: "Server", value: HOST },
                        { label: "Port", value: "993" },
                        { label: "Security", value: "SSL / TLS" },
                        { label: "Username", value: "your email address" },
                        { label: "Password", value: "your password" },
                    ]}
                />
                <SettingsCard
                    icon={<Server className="h-4 w-4" />}
                    title="Incoming mail (POP3)"
                    badge="Alternative"
                    rows={[
                        { label: "Server", value: HOST },
                        { label: "Port", value: "995" },
                        { label: "Security", value: "SSL / TLS" },
                        { label: "Username", value: "your email address" },
                        { label: "Password", value: "your password" },
                    ]}
                />
                <SettingsCard
                    icon={<Mail className="h-4 w-4" />}
                    title="Outgoing mail (SMTP)"
                    badge="Submission"
                    rows={[
                        { label: "Server", value: HOST },
                        { label: "Port", value: "587" },
                        { label: "Security", value: "STARTTLS" },
                        { label: "Auth required", value: "Yes" },
                        { label: "Username", value: "your email address" },
                        { label: "Password", value: "your password" },
                    ]}
                />
                <SettingsCard
                    icon={<Shield className="h-4 w-4" />}
                    title="Outgoing mail (SMTPS)"
                    badge="Implicit TLS"
                    rows={[
                        { label: "Server", value: HOST },
                        { label: "Port", value: "465" },
                        { label: "Security", value: "SSL / TLS" },
                        { label: "Auth required", value: "Yes" },
                        { label: "Username", value: "your email address" },
                        { label: "Password", value: "your password" },
                    ]}
                />
            </div>

            <div className="rounded-lg border bg-card p-4">
                <h3 className="mb-3 font-semibold text-sm">Step-by-step guide</h3>
                <div className="mb-3 flex gap-1.5">
                    {tabs.map((t) => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setTab(t.id)}
                            className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${tab === t.id
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-muted-foreground hover:bg-accent"
                                }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                <ol className="space-y-1.5 text-sm text-muted-foreground list-decimal list-inside">
                    {tab === "thunderbird" && <>
                        <li>Open Thunderbird → <strong>Account Settings</strong> → <strong>Add Mail Account</strong></li>
                        <li>Enter your name, email address, and password, then click <strong>Configure manually</strong></li>
                        <li>Set IMAP: server <code>{HOST}</code>, port <code>993</code>, SSL/TLS</li>
                        <li>Set SMTP: server <code>{HOST}</code>, port <code>587</code>, STARTTLS</li>
                        <li>Click <strong>Done</strong> — Thunderbird will test the connection</li>
                    </>}
                    {tab === "outlook" && <>
                        <li>Open Outlook → <strong>File</strong> → <strong>Add Account</strong></li>
                        <li>Enter your email, click <strong>Advanced options</strong>, tick <strong>Let me set up my account manually</strong></li>
                        <li>Choose <strong>IMAP</strong> as the account type</li>
                        <li>Incoming: <code>{HOST}</code> · port <code>993</code> · SSL/TLS</li>
                        <li>Outgoing: <code>{HOST}</code> · port <code>587</code> · STARTTLS</li>
                        <li>Enter your password and click <strong>Connect</strong></li>
                    </>}
                    {tab === "ios" && <>
                        <li>Go to <strong>Settings</strong> → <strong>Mail</strong> → <strong>Accounts</strong> → <strong>Add Account</strong></li>
                        <li>Tap <strong>Other</strong> → <strong>Add Mail Account</strong></li>
                        <li>Enter your name, email address, and password</li>
                        <li>Choose <strong>IMAP</strong>; set Incoming host <code>{HOST}</code> · port <code>993</code> · SSL</li>
                        <li>Outgoing host <code>{HOST}</code> · port <code>587</code> · SSL</li>
                        <li>Tap <strong>Save</strong></li>
                    </>}
                    {tab === "android" && <>
                        <li>Open the Gmail app → Menu → <strong>Add account</strong> → <strong>Other</strong></li>
                        <li>Enter your email address and tap <strong>Manual setup</strong></li>
                        <li>Choose <strong>Personal (IMAP)</strong></li>
                        <li>Incoming: <code>{HOST}</code> · port <code>993</code> · SSL/TLS</li>
                        <li>Outgoing: <code>{HOST}</code> · port <code>587</code> · STARTTLS</li>
                        <li>Enter your password and tap <strong>Next</strong> to verify</li>
                    </>}
                </ol>
            </div>

            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
                <strong>Note:</strong> When asked for a username, always enter your full email address
                (e.g. <code>alice@yourdomain.com</code>), not just the local part.
            </div>
        </div>
    );
}
